import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { postData } from '../api/api';
import { useTranslation } from 'react-i18next';
import { LOGIN, FORGOTPASSWORDAPI, RESETPASSWORDAPI, RESENDOTP } from '../helpers/Constant';
import StepProgressBar from '../common/stepBar';

function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const pendingStep = 1;
  const [userId, setUserId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const otpRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ];

  // Clear OTP error when user types
  useEffect(() => {
    if (formErrors.otp && otp.some(digit => digit !== '')) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.otp;
        return newErrors;
      });
    }
  }, [otp, formErrors.otp]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    // Clear general errors when typing
    if (formErrors.general) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.general;
        return newErrors;
      });
    }
  };

  const validateEmailStep = () => {
    const errors = {};
    if (!formData.email.trim()) {
      errors.email = t('Email is required.');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('Invalid email address.');
    }
    return errors;
  };

  const validatePasswordStep = () => {
    const errors = {};
    const combinedOtp = otp.join('');
    
    if (!combinedOtp.trim()) {
      errors.otp = t('OTP is required.');
    } else if (combinedOtp.length !== 6) {
      errors.otp = t('OTP must be 6 digits.');
    }

    if (!formData.password.trim()) {
      errors.password = t('Password is required.');
    } else if (formData.password.length < 8) {
      errors.password = t('Password must be at least 8 characters.');
    }
    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = t('Please confirm your password.');
    } else if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = t('Passwords do not match.');
    }

    return errors;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const errors = validateEmailStep();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setLoading(true);
    try {
      const response = await postData(FORGOTPASSWORDAPI, { 
        email: formData.email.toLowerCase() 
      });
      
      if (response.status === 200) {
        setUserId(response.data.user);
        setStep(2);
        setSuccessMessage(t('OTP sent to your email'));
        
        setFormErrors({});
      } else {
        setFormErrors({ general: response.message || t('Failed to send OTP') });
      }
    } catch (error) {
      setFormErrors({ 
        general: error.response?.data?.message || t('An unexpected error occurred') 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      const response = await postData(RESENDOTP, { 
        user: userId,
        type: 'password'
      });
      
      if (response.status === 200) {
        const tempMessage = t('New OTP sent to your email');
        setSuccessMessage(tempMessage);
        
        setTimeout(() => {
          setSuccessMessage(prev => prev === tempMessage ? '' : prev);
        }, 3000);
      } else {
        setFormErrors({ general: response.message || t('Failed to resend OTP') });
      }
    } catch (error) {
      setFormErrors({ 
        general: error.response?.data?.message || t('An unexpected error occurred') 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const errors = validatePasswordStep();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const combinedOtp = otp.join(''); 
    
    setLoading(true);
    try {
      const response = await postData(RESETPASSWORDAPI, { 
        user: userId,
        otp: Number(combinedOtp),
        password: formData.password
      });
      
      if (response.status === 200) {
        setSuccessMessage(t('Password reset successfully. Redirecting to login...'));
        setTimeout(() => navigate(`/${LOGIN}`), 3000);
        
        setFormErrors({});
      } else {
        setFormErrors({ general: response.message || t('Failed to reset password') });
      }
    } catch (error) {
      setFormErrors({ 
        general: error.response?.data?.message || t('An unexpected error occurred') 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value) || value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    if (value && index < 5) {
      otpRefs[index + 1].current.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current.focus();
    }
  };

  return (
    <div className='wrapper bg_paper d-flex flex-column h-100'>
      {loading && (
        <div className="loader">
          <Oval color="#daff00" secondaryColor="#ffffff" strokeWidth={3} strokeWidthSecondary={3} height={50} width={50} />
        </div>
      )}

      <div className='d-flex justify-content-center'>
        <StepProgressBar pendingStep={pendingStep} />
      </div>

      <div className='log_in_wrapper flex-fill d-flex flex-column'>
        <div className='logo_wrapper flex-fill d-flex justify-content-center align-items-center py-3'>
          <img src='../images/muvelogo.png' alt="MUVE" />
        </div>
        
        <div className='auth_section otp_section bg-primary d-grid gap-3 p-3 rounded-4 w-100 mx-auto'>
            <h3 className='text-white fw-bold text-center'>
              {step === 1 ? t('Forgot Password') : t('Reset Password')}
            </h3>
            
            {step === 1 ? (
              <div className='login-inner'>
                <p className='text-light text-center mb-4'>
                  {t('Enter your email to receive a password reset OTP')}
                </p>
                <form autoComplete='off' className='reset_password_form' onSubmit={handleSendOTP} noValidate>
                  <div className='form_field position-relative mb-4'>
                    <input type="email" name="email" id="email" className={`w-100 px-3 py-2 ${formErrors.email ? 'border-danger' : 'border'}`} placeholder={t('Email Address')} value={formData.email} onChange={handleChange}/>
                    
                    {formErrors.email && (
                      <p className='field_error'>{formErrors.email}</p>
                    )}
                    
                    {formErrors.general && (
                      <p className="field_error text-center">
                        {formErrors.general}
                      </p>
                    )}
                  
                    {successMessage && (
                      <p className="text-warning text-center mb-3">
                        {successMessage}
                      </p>
                    )}
                  </div>

            
                  <button type='submit' className='text-white w-100 guest_button rounded-3 py-2' disabled={loading}>
                    {t('Send OTP')}
                  </button>

                </form>
              </div>
            ) : (
              <>
                <p className='text-light text-center mb-4'>
                  {t('Enter the OTP sent to')} {formData.email}
                </p>
                
                <form autoComplete='off' onSubmit={handleResetPassword} noValidate>
                  <div className='otp-inputs d-flex justify-content-center mb-3 position-relative'>
                    {otp.map((digit, index) => (
                      <input key={index} type="text" maxLength={1} value={digit} ref={otpRefs[index]}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className={`otp-digit mx-1 text-center ${formErrors.otp ? 'border-danger' : ''}`}
                        autoFocus={index === 0}
                      />
                    ))}
                            
                  {formErrors.otp && (
                    <p className='field_error text-center mb-3'>
                      {formErrors.otp}
                    </p>
                  )}
                  
                  {successMessage && successMessage.includes('Password reset successfully') && (
                    <p className="field_error text-warning text-center mb-3">
                      {successMessage}
                    </p>
                  )}
                  
                  {successMessage && successMessage.includes('New OTP sent') && (
                    <p className="field_error text-warning text-center mb-3">
                      {successMessage}
                    </p>
                  )}

                  {formErrors.general && (
                    <p className="field_error text-center mb-3">
                      {formErrors.general}
                    </p>
                  )}
                  </div>
            
                  <div className='form_field position-relative mb-3 reset_password_form'>
                    <input  type="password" name="password" id="password"
                      className={`w-100 px-3 py-2 ${formErrors.password ? 'border-danger' : 'border'}`} 
                      placeholder={t('New Password')} 
                      value={formData.password}
                      onChange={handleChange}
                    />
                    {formErrors.password && (
                      <p className='field_error mt-1 mb-0'>{formErrors.password}</p>
                    )}
                  </div>

                  <div className='form_field position-relative mb-3 reset_password_form'>
                    <input type="password" name="confirmPassword" id="confirmPassword"
                      className={`w-100 px-3 py-2 ${formErrors.confirmPassword ? 'border-danger' : 'border'}`} 
                      placeholder={t('Confirm Password')} 
                      value={formData.confirmPassword}
                      onChange={handleChange}
                    />
                    {formErrors.confirmPassword && (
                      <p className='field_error mt-1 mb-0'>{formErrors.confirmPassword}</p>
                    )}
                  </div>
                  <button type='button' className='text-warning bg-transparent border-0 flex-grow-1 text-center d-block ms-auto pt-0'
                    onClick={handleResendOTP}
                    disabled={loading}
                  >
                    {t('Resend OTP')}
                  </button>
                  
                  <div className='d-flex justify-content-between gap-3'>
                    <button type='submit' className='text-white guest_button rounded-3 py-2 flex-grow-1' disabled={loading}>
                      {t('Reset Password')}
                    </button>
                  </div>
                </form>
              </>
            )}
            
            <div className='text-center'>
              <button className='text-warning bg-transparent border-0 p-0' onClick={() => navigate(`/${LOGIN}`)}>
                {t('Back to Login')}
              </button>
            </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword;